import React from 'react';
import { ArrowUpDown, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UserStatistics } from '../../hooks/useStatistics';

interface UserStatisticsTableProps {
  users: UserStatistics[];
  onUserClick?: (userId: string) => void;
}

type SortField = 'userName' | 'totalAttendanceDays' | 'totalTasksCompleted' | 'totalTasksCreated' | 'totalTasksAssigned' | 'overdueTasks' | 'completionRate';
type SortOrder = 'asc' | 'desc';

export const UserStatisticsTable: React.FC<UserStatisticsTableProps> = ({
  users,
  onUserClick
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = React.useState<SortField>('completionRate');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedUsers = React.useMemo(() => {
    return [...users].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' 
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
  }, [users, sortField, sortOrder]);

  const SortButton: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
    >
      {label}
      {sortField === field && (
        <ArrowUpDown className={`w-3 h-3 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
      )}
    </button>
  );

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('statistics.ui.noUserData')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[100px]">
              <SortButton field="userName" label={t('statistics.ui.user')} />
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              <SortButton field="totalAttendanceDays" label={t('statistics.ui.attendance')} />
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[100px]">
              <SortButton field="totalTasksCompleted" label={t('statistics.ui.completed')} />
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              <SortButton field="totalTasksCreated" label={t('statistics.ui.created')} />
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              <SortButton field="totalTasksAssigned" label={t('statistics.ui.assigned')} />
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
              <SortButton field="overdueTasks" label={t('statistics.ui.overdue')} />
            </th>
            <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[100px]">
              <SortButton field="completionRate" label={t('statistics.ui.rate')} />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.map((user) => (
            <tr 
              key={user.userId}
              onClick={() => onUserClick?.(user.userId)}
              className={`border-b border-gray-100 dark:border-gray-800 transition-colors ${
                onUserClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''
              }`}
            >
              <td className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {user.userName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.userEmail}
                    </p>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                  {user.totalAttendanceDays}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  {user.totalTasksCompleted}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                  {user.totalTasksCreated}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                  {user.totalTasksAssigned ?? 0}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                  user.overdueTasks > 0 
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                  {user.overdueTasks}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        user.completionRate >= 80 ? 'bg-green-500' :
                        user.completionRate >= 50 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${user.completionRate}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user.completionRate}%
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserStatisticsTable;
